import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {CopyIcon, DuplicateIcon, XIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'

import {MENU_LABELS} from './menu-labels'
import type {Project} from './types'

interface ProjectRowActionMenuProps {
  project: Project
  onCopyProject: (project: Project) => void
  onCopyAsTemplate: (project: Project) => void
  onUnlinkProject: (project: Project) => void
}

/**
 * Kebab action menu for a single project row in the repo projects index. Mirrors
 * the repo-context branch of `Memex::ProjectList::ActionMenuComponent`.
 */
export function ProjectRowActionMenu({
  project,
  onCopyProject,
  onCopyAsTemplate,
  onUnlinkProject,
}: ProjectRowActionMenuProps) {
  const showCopy = project.viewerCanCopy
  const showCopyAsTemplate = project.viewerCanCopyAsTemplate
  const showRemove = project.viewerCanWrite
  const showDivider = (showCopy || showCopyAsTemplate) && showRemove

  return (
    <ListItemActionBar
      label={`Actions for ${project.title}`}
      staticMenuActions={[
        ...(showCopy
          ? [
              {
                key: 'copy',
                render: () => (
                  <ActionList.Item onSelect={() => onCopyProject(project)}>
                    <ActionList.LeadingVisual>
                      <CopyIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.copy}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(showCopyAsTemplate
          ? [
              {
                key: 'copy-as-template',
                render: () => (
                  <ActionList.Item onSelect={() => onCopyAsTemplate(project)}>
                    <ActionList.LeadingVisual>
                      <DuplicateIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.copyAsTemplate}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(showDivider
          ? [
              {
                key: 'divider',
                render: () => <ActionList.Divider />,
              },
            ]
          : []),
        ...(showRemove
          ? [
              {
                key: 'remove',
                render: () => (
                  <ActionList.Item variant="danger" onSelect={() => onUnlinkProject(project)}>
                    <ActionList.LeadingVisual>
                      <XIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.remove}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
      ]}
    />
  )
}
